/**
 * Goal tracking types for the chat-ui client.
 * Maps to SSE events emitted by the goal tracking system (SP-0 through SP-3).
 */

export type GoalPhase =
  | "idle"
  | "planning"
  | "executing"
  | "verifying"
  | "complete"
  | "paused";

export type PauseReason =
  | "user_paused"
  | "budget_exhausted"
  | "stall_detected"
  | "infra_error"
  | "blocked";

export type PlanItemStatus = "pending" | "in_progress" | "completed" | "skipped";

export interface GoalPlanItem {
  id: string;
  content: string;
  status: PlanItemStatus;
  verified_at: string | null;
}

export interface GoalBudget {
  max_cost_usd: number;
  max_turns: number;
  max_duration_s: number;
}

export interface GoalSpent {
  cost_usd: number;
  turns: number;
  duration_s: number;
}

/** Client-side goal state, updated from SSE events. */
export interface GoalState {
  goalId: string;
  objective: string;
  phase: GoalPhase;
  pauseReason: PauseReason | null;
  plan: GoalPlanItem[];
  planVersion: number;
  budget: GoalBudget;
  spent: GoalSpent;
  strategyNote: string | null;
  budgetWarningDimension: string | null;
}

/** Initial (empty) goal state — no active goal. */
export const EMPTY_GOAL_STATE: GoalState | null = null;

// ---------------------------------------------------------------------------
// SSE event frame shapes (as received from the wire)
// ---------------------------------------------------------------------------

export interface GoalActivatedEvent {
  kind: "goal_activated";
  goal_id: string;
  objective: string;
  from_phase: GoalPhase;
  to_phase: GoalPhase;
}

export interface GoalPhaseChangedEvent {
  kind: "goal_phase_changed" | "goal_paused" | "goal_resumed";
  goal_id: string;
  from_phase: GoalPhase;
  to_phase: GoalPhase;
  reason?: string;
  unmet_items?: Array<{ id: string; gap: string }>;
}

export interface GoalPlanUpdatedEvent {
  kind: "goal_plan_updated";
  goal_id: string;
  plan_version: number;
  item_count: number;
}

export interface GoalCompleteEvent {
  kind: "goal_complete";
  goal_id: string;
  to_phase: "complete";
  verdict?: string;
}

export interface GoalCancelledEvent {
  kind: "goal_cancelled";
  goal_id: string;
  from_phase: GoalPhase;
  to_phase: "idle";
}

export interface GoalBudgetWarningEvent {
  kind: "goal_budget_warning";
  goal_id: string;
  dimension: string;
  spent: number | string;
  max: number | string;
}

export type GoalSSEEvent =
  | GoalActivatedEvent
  | GoalPhaseChangedEvent
  | GoalPlanUpdatedEvent
  | GoalCompleteEvent
  | GoalCancelledEvent
  | GoalBudgetWarningEvent;

/** Check if an SSE frame is a goal event. */
export function isGoalEvent(frame: { kind?: string }): boolean {
  return typeof frame.kind === "string" && frame.kind.startsWith("goal_");
}
